<template>
  <div id="apply">
    <div class="container">
      <Step1 v-show="step === 1" />
      <Step2 v-show="step === 2" @confirm="handleConfirmOpen" />
      <Step3 v-show="step === 3" @confirm="handleConfirmOpen" />
      <Step4 v-show="step === 4" @confirm="handleConfirmOpen" />
      <Step5 v-show="step === 5" @confirm="handleConfirmOpen" />

      <UiModalConfirm iconType="tip_2" ref="exit" :options="confirmOptions" @apply="handleApply" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, provide } from 'vue'
import { useAppStore } from '@/store'
import { usePrompt } from '@/composables/usePrompt'
import * as api from '@/service/index'
import { readApplyMock, withMockedApplyStatus } from '@/utils/applyMock'
import Step1 from './step1.vue'
import Step2 from './step2.vue'
import Step3 from './step3.vue'
import Step4 from './step4.vue'
import Step5 from './step5.vue'
import UiModalConfirm from '@/components/ui-modal-confirm'

const appStore = useAppStore()
const token = computed(() => appStore.token)

const step = ref(1)

const apply_info = ref({})
const submit_info = ref({})

const temp_player_list = ref([])
const loading = ref(false)
const confirmOptions = ref({
  tip: '报名尚未完成，是否退出当前页面'
})

// 模板 ref
const exit = ref(null)

const { showPrompt } = usePrompt()

provide('reqApplyInfo', reqApplyInfo)
provide('handleStep', handleStep)
provide('handleStepSubmit', handleStepSubmit)
provide('apply_info', () => apply_info.value)
provide('updateInfo', updateInfo)
provide('info_player', () => temp_player_list.value)

watch(
  token,
  (newVal) => {
    if (!newVal) return
    reqApplyInfo()
  },
  { immediate: true }
)

// 报名期 mock(localhost 调试), 解析逻辑见 @/utils/applyMock
const mockApply = readApplyMock()
provide('mockApply', mockApply)

function reqApplyInfo() {
  if (!token.value) {
    showPrompt('未检测到token')
    return
  }

  api
    .reqApplyInfo({
      token: token.value
    })
    .then((r) => {
      if (r.status === 200) {
        apply_info.value = withMockedApplyStatus(r.data, mockApply)
        // 显示进度的队员列表
        getTempPlayerList(apply_info.value)
      } else {
        showPrompt(r.data)
      }
    })
}

function getTempPlayerList(data) {
  let list = []
  list.push({
    uid: data.uid,
    nick: data.nick,
    label: '队长'
  })
  let player = data.player || []
  if (player.length !== 0) {
    player.forEach((item) => {
      list.push({
        uid: item.uid,
        nick: item.nick,
        label: item.player_type === 1 ? '队员' : '替补'
      })
    })
  }

  temp_player_list.value = list
}

function handleStep(stepVal) {
  step.value = stepVal

  // 触发导航栏的 二级导航
  if (stepVal >= 2) {
    let obj = {
      level: 2,
      name: '报名',
      backFn: () => {
        step.value = 1
        let obj_back = { level: 1, name: '' }
        appStore.navStatus = obj_back
      }
    }
    appStore.navStatus = obj
  }
}

function tidyPlayerInfo(data) {
  temp_player_list.value = data

  let obj = {
    nick: '',
    contact: '',
    address: '',
    player: []
  }
  data.forEach((item) => {
    if (item.position === 'leader') {
      obj.nick = item.nick
      obj.contact = item.contact
      obj.address = item.address
    } else {
      let type = 1
      switch (item.position) {
        case 'player':
          type = 1
          break
        case 'substitute':
          type = 2
          break
      }

      obj.player.push({
        player_type: type,
        uid: String(item.uid),
        address: item.address,
        nick: item.nick
      })
    }
  })

  return obj
}

function tidyQuestionInfo(data) {
  let obj = {
    question: []
  }

  data.forEach((item) => {
    obj.question.push({
      question_id: String(item.id),
      answer: Number(item.state)
    })
  })

  return obj
}

function tidyTeamInfo(data) {
  let obj = {
    team_name: data.name,
    team_intro: data.intro,
    team_desc: data.player_intro
  }

  return obj
}

function handleStepSubmit(stepVal, data) {
  switch (stepVal) {
    case 2: {
      const p = tidyPlayerInfo(data)
      submit_info.value = { ...submit_info.value, ...p }
      break
    }
    case 3: {
      const q = tidyQuestionInfo(data)
      submit_info.value = { ...submit_info.value, ...q }
      break
    }
    case 4: {
      const t = tidyTeamInfo(data)
      submit_info.value = { ...submit_info.value, ...t }
      if (mockApply?.bypassValidate) {
        // mock 模式跳过真实提交, 直接进 step5 看进度页
        handleStep(5)
      } else {
        // 提交所有报名信息
        submit()
      }
      break
    }
  }
}

function submit() {
  if (loading.value) return

  if(!token.value) return;
  loading.value = true
  api.submitApply(token.value, submit_info.value).then((r) => {
    if (r.status === 200 && r.data.success) {
      handleStep(5)
      // 报名完成后，更新报名信息
      reqApplyInfo()
    } else {
      showPrompt(r.data)
    }

    loading.value = false
  })
}

function updateInfo(key, val) {
  apply_info.value[key] = val
}

function handleConfirmOpen() {
  exit.value.showModal()
}

function handleApply() {
  handleStep(1)
}
</script>

<style lang="scss">
#apply {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  .container {
    flex: 1;
    height: 100%;
    min-height: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    width: 100%;

    .role,
    .role1 {
      background-position: center;
      background-repeat: no-repeat;
      background-size: contain;
      position: absolute;
    }
    .role {
      width: get_vh(216);
      height: get_vh(500);
      background-image: url('https://website.xdcdn.net/poster/355916/sausage-match/9Lq0PsYq.png');
      left: get_vh(70);
      bottom: 0;
    }
    .role1 {
      width: get_vh(300);
      height: get_vh(500);
      background-image: url('https://website.xdcdn.net/poster/355916/sausage-match/9Lq0PefF.png');
      right: 0;
      bottom: 0;
    }
  }
}
</style>
